import { Injectable } from '@angular/core';
import {
  ActionType,
  ErrorType,
  PageValidationType, ValidationError,
  ValidationResult,
} from '../../models/projects/validation.models';
import { DailyLogValidationService } from '../daily-log/daily-log-validation/daily-log-validation.service';
import { WeatherLogValidationService } from '../weather-readings/weather-log-validation.service';
import { DailyLogService } from '../daily-log/daily-log.service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { WeatherReadingsService } from '../weather-readings/weather-readings.service';
import { ConsumablesService } from '../consumables/consumables.service';
import { VesselCrewService } from '../vessel-crew/vessel-crew.service';
import { CompleteDayService } from '../complete-day/complete-day.service';
import {
  DailyLogOperationInstance,
  DailyOperationLogRecord
} from '../../models/projects/operation.models';
import { OperationService } from '../operations/operation/operation.service';
import { LockedDayService } from '../locked-day/locked-day.service';
import { DailyObservationService } from '../daily-observation/daily-observation.service';
import { DailyLegLoadValidationService } from '../daily-leg-loads/daily-leg-load-validation.service';
import { DailyLegLoadService } from '../daily-leg-loads/daily-leg-load.service';
import { JackingActivityValidationService } from '../jacking-activity/jacking-activity-validation.service';
import { FieldPositioningService } from '../field-positioning/field-positioning.service';
import { OnSiteMovementService } from '../on-site-movement/on-site-movement.service';
import { MorningMinutesReportService } from '../morning-minutes/morning-minutes-report.service';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { VoyageLogService } from '../voyage-log/voyage-log.service';
import { cloneDeep } from 'lodash';
import { DateService } from '../date/date.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { JackingActivityRobFigureService } from '../jacking-activity/jacking-activity-rob-figure.service';
import {JackingV2Service} from '../jacking-v2/jacking-v2.service';
import {AppConfig} from '../../app/app.config';
import {HttpClient} from '@angular/common/http';
import { VesselService } from '../vessel/vessel.service';

@Injectable()
export class ValidationService {
  config = AppConfig.settings;

  constructor(
    private dailyLogValidationService: DailyLogValidationService,
    private dailyLogService: DailyLogService,
    private weatherReadingValidationService: WeatherLogValidationService,
    private weatherReadingService: WeatherReadingsService,
    private consumableService: ConsumablesService,
    private vesselCrewService: VesselCrewService,
    private completeDayService: CompleteDayService,
    private operationService: OperationService,
    private lockedDayService: LockedDayService,
    private fieldPositioningService: FieldPositioningService,
    private robFigureService: JackingActivityRobFigureService,
    private dailyObservationService: DailyObservationService,
    private dailyLegLoadService: DailyLegLoadService,
    private dailyLegLoadValidationService: DailyLegLoadValidationService,
    private jackingActivityValidationService: JackingActivityValidationService,
    private onSiteMovementService: OnSiteMovementService,
    private morningMinutesReportService: MorningMinutesReportService,
    private timeZoneService: TimeZoneService,
    private voyageLogService: VoyageLogService,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private http: HttpClient,
    private vesselService: VesselService
  ) {
  }

  async validateAppViaApi(dailyLogDate?: PtLuxonDate): Promise<ValidationResult> {
    const result = new ValidationResult();

    if (!dailyLogDate?.dateTime) {
      return result;
    }

    const dateLong = dailyLogDate.dateTime.toMillis();

    const base = AppConfig.settings.apiUrl.endsWith('/')
      ? AppConfig.settings.apiUrl
      : `${AppConfig.settings.apiUrl}/`;

    const url = `${base}api/appdata/${dateLong}/validate-all`;

    const dto: any = await this.http.get<any>(url).toPromise();

    // hydrate ValidationResult (makes addError() work)
    Object.assign(result, dto);

    // hydrate errors (optional, but keeps types consistent)
    result.errors = (dto?.errors ?? []).map((e: any) => Object.assign(new ValidationError(), e));

    return this.applyVesselSectionFilters(result);
  }

  async validateDailyLog(logs: DailyOperationLogRecord[], dailyLogDate?: PtLuxonDate, instances?: DailyLogOperationInstance[]): Promise<ValidationResult> {
    const validationResult = await this.validateAppViaApi(dailyLogDate);
    const updatedInstances = instances ? instances : await this.operationService.getOperationsFromLogs(logs);
    const result = await this.dailyLogValidationService.validateInstances(updatedInstances, dailyLogDate, validationResult, logs);

    return this.applyVesselSectionFilters(result);
  }

  public async validateDailyObservationsOnPage(ptLuxonDate: PtLuxonDate, existingResult: ValidationResult): Promise<ValidationResult> {
    if (existingResult.errors.every(e => e.pageValidationType !== PageValidationType.DailyObservations)) {
      await this.dailyObservationService.getObservationDataForDay(ptLuxonDate).then(value => {
        if (!value.lastTwentyFourHours) {
          existingResult.addError(
            `'Last Twenty Four Hours' has not been filled in'`,
            ErrorType.Warning,
            ActionType.DailyObservations,
            null,
            PageValidationType.DailyObservations);
        }

        if (!value.nextTwentyFourHours) {
          existingResult.addError(
            `'Next Twenty Four Hours' has not been filled in'`,
            ErrorType.Warning,
            ActionType.DailyObservations,
            null,
            PageValidationType.DailyObservations);
        }

        if (!value.seajacksActivitiesDeck) {
          existingResult.addError(
            `'Cadeler Activities Deck' has not been filled in'`,
            ErrorType.Warning,
            ActionType.DailyObservations,
            null,
            PageValidationType.DailyObservations);
        }

        if (!value.seajacksActivitiesEngineering) {
          existingResult.addError(
            `'Cadeler Activities Engineering' has not been filled in'`,
            ErrorType.Warning,
            ActionType.DailyObservations,
            null,
            PageValidationType.DailyObservations);
        }

        if (!value.extraWork) {
          existingResult.addError(
            `'Extra Work' has not been filled in'`,
            ErrorType.Warning,
            ActionType.DailyObservations,
            null,
            PageValidationType.DailyObservations);
        }


      });
    }

    return existingResult;
  }

  public async validateJackingActivities(
    ptLuxonDate: PtLuxonDate,
    existingResult: ValidationResult,
    currentPage?: PageValidationType,
  ): Promise<ValidationResult> {
    if (await this.vesselService.isCurrentVesselNonJacking()) {
      return existingResult;
    }

    if (existingResult.errors.every(e => e.pageValidationType !== PageValidationType.JackingActivity)) {
      await this.jackingActivityValidationService.ValidateForDay(ptLuxonDate).then(value => {
        if (!value) {
          existingResult.addError(
            'There are outstanding Jacking Activities which need to be completed',
            currentPage === PageValidationType.DailyLog ? ErrorType.Error : ErrorType.Warning,
            null,
            null,
            PageValidationType.JackingActivity
          );
        }
      });
    }

    return existingResult;
  }

  public async validateFieldPositioning(
    ptLuxonDate: PtLuxonDate,
    existingResult: ValidationResult,
  ): Promise<ValidationResult> {
    // local offset needs to be added so that the correct date is received on the API
    const timeZone = await this.timeZoneService.getEditorLocally({
      dateTime: ptLuxonDate.dateTime,
      dateAmbiguous: true
    });

    if (timeZone) {
      // no timezone implies user has gone back to a day before they started logging - so we don't care about validation
      const outstandingFieldPositioning = await this.fieldPositioningService.checkOutstandingFieldPositioning(cloneDeep(ptLuxonDate.dateTime));
      if (outstandingFieldPositioning) {
        existingResult.addError(
          'There are outstanding Field Positioning records.',
          ErrorType.Warning,
          ActionType.FieldPositioningOutstanding,
          null,
          PageValidationType.FieldPositioning
        );
      }
    }

    return existingResult;
  }

  public async validateRobFuelFigures(
    ptLuxonDate: PtLuxonDate,
    existingResult: ValidationResult,
  ): Promise<ValidationResult> {
    if (await this.vesselService.isCurrentVesselNonJacking()) {
      return existingResult;
    }

    // local offset needs to be added so that the correct date is received on the API
    const timeZone = await this.timeZoneService.getEditorLocally({
      dateTime: ptLuxonDate.dateTime,
      dateAmbiguous: true
    });

    if (timeZone) {
      // no timezone implies user has gone back to a day before they started logging - so we don't care about validation
      const outstandingRobFuelFigures = await this.robFigureService.checkOutstandingRobFuelFigures(cloneDeep(ptLuxonDate.dateTime));
      if (outstandingRobFuelFigures) {
        existingResult.addError(
          'There are outstanding ROB fuel figures.',
          ErrorType.Warning,
          ActionType.RobFuelFigures,
          null,
          PageValidationType.RobFuelFigures
        );
      }
    }

    return existingResult;
  }

  public async validateOnSiteMovement(
    date: PtLuxonDate,
    existingResult: ValidationResult,
    currentPage?: PageValidationType,
  ): Promise<ValidationResult> {
    await this.onSiteMovementService.validateOnSiteMovementForDailyLog(date).then(value => {
      if (!value) {
        existingResult.addError(
          'There are outstanding On-Site Movement records.',
          currentPage === PageValidationType.DailyLog ? ErrorType.Error : ErrorType.Warning,
          null,
          null,
          PageValidationType.JackingActivity
        );
      }
    });

    return existingResult;
  }

  public async validateMorningMinutes(
    date: PtLuxonDate,
    existingResult: ValidationResult,
  ): Promise<ValidationResult> {

    const reportGenerationRequired = await this.morningMinutesReportService.checkDataModified(date);

    if (reportGenerationRequired) {
      existingResult.addError(
        'Some fields on the MoM report have been modified since the report was last generated. Re-generating the report is required.',
        ErrorType.Error,
        ActionType.MorningMinutes,
        null,
        PageValidationType.MorningMinutes
      );
    }

    return existingResult;
  }

  public async validateTimeZones(
    date: PtLuxonDate,
    existingResult: ValidationResult,
  ): Promise<ValidationResult> {
    const endOfDay = await this.dateService.getEndOfDayLocally(date.dateTime);

    const timeZones = await this.timeZoneService.getAllTimeZones();

    // timeZones = await this.dateParserService.deserializeDates(timeZones, true, this.timeZoneService);

    if (timeZones.some(e => e.timeOfChange >= date.dateTime && e.timeOfChange < endOfDay.dateTime)) {
      existingResult.addError(
        'A Time Zone change has been logged - this will cause the DPR to have more or less than the standard 24 hours in the day.',
        ErrorType.Warning,
        ActionType.Timezone,
        null,
        null
      );
    }

    return existingResult;
  }

  private async applyVesselSectionFilters(result: ValidationResult): Promise<ValidationResult> {
    if (!result || !(await this.vesselService.isCurrentVesselNonJacking())) {
      return result;
    }

    const hiddenPageTypes = [
      PageValidationType.WeatherReading,
      PageValidationType.Consumables,
      PageValidationType.JackingActivity,
      PageValidationType.DailyLegLoad,
      PageValidationType.RobFuelFigures,
      PageValidationType.VoyageLog
    ];

    const hiddenActionTypes = [
      ActionType.Weather,
      ActionType.Consumable,
      ActionType.DailyLegLoad,
      ActionType.RobFuelFigures,
      ActionType.VoyageLog
    ];

    result.errors = (result.errors || []).filter(error =>
      !hiddenPageTypes.includes(error.pageValidationType) &&
      !hiddenActionTypes.includes(error.actionType)
    );
    result.isValid = !result.errors.some(error => error.type === ErrorType.Error || error.type === ErrorType.Default);

    return result;
  }
}



// WEBPACK FOOTER //
// ./src/services/validation/validation.service.ts